import { z } from "zod";
import { EditReason } from "./enums";

/**
 * Valid NFC Tag UID byte lengths:
 * - 4 bytes: Single-size UID (e.g. MIFARE Classic / Ultralight) = 8 hex chars
 * - 7 bytes: Double-size UID (e.g. NTAG213 / NTAG215) = 14 hex chars
 * - 10 bytes: Triple-size UID = 20 hex chars
 */
const VALID_HEX_LENGTHS = new Set([8, 14, 20]);

/**
 * NFC Tag UID Normalization helper.
 *
 * Strips colons, hyphens, and whitespace, converts to uppercase hex.
 * Requires valid hexadecimal characters AND length ∈ {8, 14, 20} (even bytes).
 */
export function normalizeTagUid(raw: string): string {
  if (typeof raw !== "string") {
    throw new Error("Tag UID must be a string");
  }
  const cleaned = raw.replace(/[:\s-]/g, "").toUpperCase();
  if (cleaned.length === 0) {
    throw new Error("Tag UID cannot be empty");
  }
  if (!/^[0-9A-F]+$/.test(cleaned)) {
    throw new Error("Tag UID must be valid hexadecimal characters");
  }
  if (!VALID_HEX_LENGTHS.has(cleaned.length)) {
    throw new Error(
      `Invalid Tag UID length (${cleaned.length} hex chars). Expected 8, 14, or 20 hex characters (4, 7, or 10 bytes)`,
    );
  }
  return cleaned;
}

/** Zod validator schema for tag UID that pipes through normalizeTagUid with graceful issue handling */
export const TagUidSchema = z.string().superRefine((val, ctx) => {
  try {
    normalizeTagUid(val);
  } catch (err: unknown) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: err instanceof Error ? err.message : String(err),
    });
  }
}).transform((val) => normalizeTagUid(val));

export const PunchType = z.enum(["IN", "OUT", "LUNCH_OUT", "LUNCH_IN"]);
export type PunchType = z.infer<typeof PunchType>;

export const PunchSource = z.enum(["NFC", "MANUAL", "NO_TAG", "LUNCH"]);
export type PunchSource = z.infer<typeof PunchSource>;

export const TimeclockConfig = z.object({
  nfcRequired: z.boolean(),
  nfcLoginRequired: z.boolean(),      // SPEC-70 — ALWAYS emitted (audit #10)
});
export type TimeclockConfig = z.infer<typeof TimeclockConfig>;

export const TimeclockTag = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  uid: z.string(),
  label: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
});
export type TimeclockTag = z.infer<typeof TimeclockTag>;

export const NewTag = z.object({
  uid: TagUidSchema,
  label: z.string().min(1).max(100),
});
export type NewTag = z.infer<typeof NewTag>;

export const PatchTag = z.object({
  label: z.string().min(1).max(100).optional(),
  isActive: z.boolean().optional(),
}).strict().refine((d) => d.label !== undefined || d.isActive !== undefined, {
  message: "At least one field (label or isActive) is required",
});
export type PatchTag = z.infer<typeof PatchTag>;

export const TimeclockPunch = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  driverUserId: z.string().uuid(),
  driverName: z.string().optional(),
  tagId: z.string().uuid().nullable(),
  tagLabel: z.string().optional(),
  // SPEC-70 follow-up (PM-approved 2026-09-16): the tag UID is deliberately
  // NOT on the read wire. It is the presence secret the login gate verifies;
  // exposing it to every DRIVER/DISPATCHER session would let a session holder
  // satisfy the gate from anywhere (SPEC-70 r1 audit BUG #2(ii)). The admin
  // registry view (GET /timeclock/tags, OFFICE-only) is the place to see UIDs.
  punchType: PunchType,
  punchedAt: z.string().datetime(),
  source: PunchSource,
  // SPEC-50 B — the office-entered justification, now persisted on the row.
  // Optional because it is NULL for every self-service punch (NFC / NO_TAG)
  // and for pre-migration rows: migration 0019 adds the column WITHOUT a
  // backfill. Mappers surface NULL as absent, mirroring `tagLabel`.
  note: z.string().optional(),
  createdByUserId: z.string().uuid(),
  createdByName: z.string().optional(),
  createdAt: z.string().datetime(),
});
export type TimeclockPunch = z.infer<typeof TimeclockPunch>;

/** GET /timeclock/latest — SPEC-65 adds the per-day lunch flag.
 *  .default(false) tolerates a pre-SPEC-65 Worker response (rollback /
 *  deploy-order drift) instead of failing the whole parse. */
export const LatestPunchResponse = z.object({
  punch: TimeclockPunch.nullable(),
  lunchTakenToday: z.boolean().default(false),
});
export type LatestPunchResponse = z.infer<typeof LatestPunchResponse>;

// Driver Punch request (POST /timeclock/punch)
// When tenant NFC requirement is ON: tagUid mandatory (enforced at route level).
// When OFF: tagUid must be omitted (route rejects if present; records NO_TAG/NULL tag).
export const PunchRequest = z.object({
  tagUid: TagUidSchema.optional(),
  punchType: PunchType,
}).strict();
export type PunchRequest = z.infer<typeof PunchRequest>;

// Office manual punch create (POST /timeclock/punches)
export const ManualPunch = z.object({
  driverUserId: z.string().uuid(),
  tagId: z.string().uuid().optional(), // optional; office can tag a location or omit
  punchType: PunchType,
  punchedAt: z.string().datetime(),
  note: z.string().min(1).max(500),
}).strict();
export type ManualPunch = z.infer<typeof ManualPunch>;

// Office manual punch edit (PATCH /timeclock/punches/:id)
export const ManualPunchEdit = z.object({
  punchType: PunchType.optional(),
  punchedAt: z.string().datetime().optional(),
  tagId: z.string().uuid().nullable().optional(),
  note: z.string().min(1).max(500).optional(),
  reason: EditReason,
}).strict();
export type ManualPunchEdit = z.infer<typeof ManualPunchEdit>;

// Dispatcher / Office NFC tag verification (POST /timeclock/tags/verify)
export const TagVerifyRequest = z.object({
  uid: TagUidSchema,
}).strict();
export type TagVerifyRequest = z.infer<typeof TagVerifyRequest>;

export const TagVerifyResponse = z.discriminatedUnion("valid", [
  z.object({
    valid: z.literal(true),
    label: z.string(),
  }),
  z.object({
    valid: z.literal(false),
  }),
]);
export type TagVerifyResponse = z.infer<typeof TagVerifyResponse>;

